/**
 * Eyewear-camera constants shared across the forge-tryon renderer modules.
 *
 * These values are physically grounded:
 *  - EYEWEAR_CAMERA_FOV: 63° matches typical webcam vertical FOV and slightly
 *    over-covers the frame for wider webcams. Consistent with the MediaPipe
 *    avatar demo (60°). Do NOT change without re-tuning the landmark scale.
 *  - VIDEO_PLANE_DEPTH: depth at which the video-backing plane sits behind
 *    glasses in scene units. Must stay consistent between the scene camera,
 *    the face occluder, and the debug overlay.
 *  - LM_TO_SCENE_SCALE: derived landmark-to-scene scale factor, formerly
 *    recomputed independently in scene.ts, occluder.ts and debug-overlay.ts.
 *
 * Single source of truth — do NOT duplicate these in scene.ts, occluder.ts,
 * or debug-overlay.ts.
 */

/** Camera FOV in degrees used by all eyewear widgets. Source: scene.ts (Phase 12.A.6). */
export const EYEWEAR_CAMERA_FOV = 63;

/** Depth in scene units of the video background plane. Source: scene.ts (Phase 12.A.6). */
export const VIDEO_PLANE_DEPTH = 500;

/**
 * Landmark-to-scene scale factor.
 * Derived: 2 × VIDEO_PLANE_DEPTH × tan(EYEWEAR_CAMERA_FOV/2 in radians).
 * Previously computed independently in scene.ts, occluder.ts, and debug-overlay.ts —
 * now canonical here.
 */
export const LM_TO_SCENE_SCALE =
  2 * VIDEO_PLANE_DEPTH * Math.tan(((EYEWEAR_CAMERA_FOV / 2) * Math.PI) / 180);

/**
 * Compute the pixels-to-scene-units scale factor for landmark coordinates
 * at the video plane.
 *
 * Equivalent to: LM_TO_SCENE_SCALE / videoWidth.
 *
 * @param videoWidth - Width of the video element in pixels.
 */
export const computeLmToSceneScale = (videoWidth: number): number =>
  LM_TO_SCENE_SCALE / videoWidth;
